import { type OptionWithValue } from '@efdigital/ll-admin-v2/utils/quiz-choices';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import BsDropdown from 'ember-bootstrap/components/bs-dropdown';

export interface OptionsWizardSignature<T extends OptionWithValue> {
  Args: {
    options: T[];
    updateOptions: (options: T[]) => void;
    hasScore?: boolean;
  };
  Element: HTMLDivElement;
}

export class OptionsWizard<T extends OptionWithValue> extends Component<
  OptionsWizardSignature<T>
> {
  sortAlphabetically = (close: () => void) => {
    const sorted = [...this.args.options].sort((a, b) =>
      a.value.localeCompare(b.value)
    );
    this.args.updateOptions(sorted);
    close();
  };

  sortByScoreAscending = (close: () => void) => {
    const sorted = [...this.args.options].sort((a, b) => {
      const scoreA = a.score ?? 0;
      const scoreB = b.score ?? 0;
      return scoreA - scoreB;
    });
    this.args.updateOptions(sorted);
    close();
  };

  sortByScoreDescending = (close: () => void) => {
    const sorted = [...this.args.options].sort((a, b) => {
      const scoreA = a.score ?? 0;
      const scoreB = b.score ?? 0;
      return scoreB - scoreA;
    });
    this.args.updateOptions(sorted);
    close();
  };

  shuffleRandomly = (close: () => void) => {
    const shuffled = [...this.args.options];
    // Fisher-Yates shuffle algorithm
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
    }
    this.args.updateOptions(shuffled);
    close();
  };

  <template>
    <BsDropdown ...attributes as |dd|>
      <dd.toggle
        class="btn btn-white btn-md py-1 btn-magical-border"
        aria-label="Options wizard"
      >
        <iconify-icon inline icon="ph:sparkle" />
        <span>Sort</span>
      </dd.toggle>

      <dd.menu as |ddm|>
        <ddm.item>
          <button
            type="button"
            class="dropdown-item"
            {{on "click" (fn this.sortAlphabetically dd.closeDropdown)}}
          >
            <iconify-icon inline icon="ph:sort-ascending-bold" />
            Sort Alphabetically
          </button>
        </ddm.item>

        {{#if @hasScore}}
          <ddm.item>
            <button
              type="button"
              class="dropdown-item"
              {{on "click" (fn this.sortByScoreAscending dd.closeDropdown)}}
            >
              <iconify-icon inline icon="ph:arrow-up-bold" />
              Sort by Score (Low to High)
            </button>
          </ddm.item>
          <ddm.item>
            <button
              type="button"
              class="dropdown-item"
              {{on "click" (fn this.sortByScoreDescending dd.closeDropdown)}}
            >
              <iconify-icon inline icon="ph:arrow-down-bold" />
              Sort by Score (High to Low)
            </button>
          </ddm.item>
        {{/if}}

        <ddm.item>
          <button
            type="button"
            class="dropdown-item"
            {{on "click" (fn this.shuffleRandomly dd.closeDropdown)}}
          >
            <iconify-icon inline icon="ph:shuffle-bold" />
            Shuffle Randomly
          </button>
        </ddm.item>
      </dd.menu>
    </BsDropdown>
  </template>
}
